import { useState } from 'react';
import type {
  AssistantAnswer,
  ProductLineChoice,
  ProductLineConfirmResult,
  ProposedIngest,
  StatementPreview,
} from '../lib/assistant.js';
import type { Turn } from '../lib/useAssistantStream.js';
import { useCarriers } from '../lib/useCarriers.js';
import { AnswerView } from './AssistantAnswer.js';

/** The turn list: the existing turns.map → TurnView loop, per-turn wiring intact. */
export function AssistantTurns({
  turns,
  busy,
  onAsk,
  onSave,
  onWatch,
  onInvestigate,
  onIngest,
  onRepreview,
  onConfirmProductLine,
  onApproveIngest,
}: {
  turns: Turn[];
  busy: boolean;
  onAsk: (q: string) => void;
  onSave: (a: AssistantAnswer) => void;
  onWatch: (question: string) => void;
  onInvestigate: (question: string) => void;
  onIngest: (turnId: number, file: File, carrierId: string, period: string) => Promise<string>;
  onRepreview: (turnId: number, file: File, carrierId: string) => Promise<void>;
  onConfirmProductLine: (
    carrierId: string,
    choice: ProductLineChoice,
  ) => Promise<ProductLineConfirmResult>;
  onApproveIngest: (turnId: number, proposal: ProposedIngest) => Promise<string>;
}) {
  return (
    <>
      {turns.map((t, i) => (
        <TurnView
          key={t.id}
          turn={t}
          onAsk={onAsk}
          onSave={onSave}
          onWatch={onWatch}
          onInvestigate={onInvestigate}
          onIngest={(file, carrierId, period) => onIngest(t.id, file, carrierId, period)}
          onRepreview={(file, carrierId) => onRepreview(t.id, file, carrierId)}
          onConfirmProductLine={onConfirmProductLine}
          onApproveIngest={(proposal) => onApproveIngest(t.id, proposal)}
          isLast={i === turns.length - 1 && !busy}
        />
      ))}
    </>
  );
}

export function EmptyState({
  onPick,
  suggestions,
}: {
  onPick: (q: string) => void;
  suggestions: string[];
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <Spark large />
      <h2 className="mt-4 text-lg font-semibold">More than a chat with your book</h2>
      <p className="mt-1 max-w-lg text-sm text-neutral-500">
        Ask in plain English — three things, one place:
      </p>
      <div className="mt-5 grid w-full max-w-2xl gap-2 text-left sm:grid-cols-3">
        {[
          {
            t: 'Ask your book',
            d: '“Who dropped this month, and what were they worth?” — answered from your data, with the numbers behind it.',
          },
          {
            t: 'Ask how it works',
            d: '“What does owed coverage mean?” “How do I set up a rate schedule?” — answered from the docs.',
          },
          {
            t: 'Get things set up',
            d: 'Set up a new carrier or draft your rates — the AI does the heavy lifting, you approve.',
          },
        ].map((cap) => (
          <div
            key={cap.t}
            className="rounded-xl border border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-900"
          >
            <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">{cap.t}</p>
            <p className="mt-1 text-xs leading-relaxed text-neutral-500">{cap.d}</p>
          </div>
        ))}
      </div>
      <p className="mt-5 text-xs font-medium text-neutral-400">Try one:</p>
      <div className="mt-6 grid w-full max-w-xl gap-2 sm:grid-cols-2">
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onPick(s)}
            className="rounded-xl border border-neutral-200 bg-white px-4 py-3 text-left text-sm text-neutral-700 transition hover:border-emerald-400 hover:bg-emerald-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:border-emerald-600/60 dark:hover:bg-emerald-950/30"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function TurnView({
  turn,
  onAsk,
  onSave,
  onWatch,
  onInvestigate,
  onIngest,
  onRepreview,
  onConfirmProductLine,
  onApproveIngest,
  isLast,
}: {
  turn: Turn;
  onAsk: (q: string) => void;
  onSave: (a: AssistantAnswer) => void;
  onWatch: (question: string) => void;
  onInvestigate: (question: string) => void;
  onIngest: (file: File, carrierId: string, period: string) => Promise<string>;
  onRepreview: (file: File, carrierId: string) => Promise<void>;
  onConfirmProductLine: (
    carrierId: string,
    choice: ProductLineChoice,
  ) => Promise<ProductLineConfirmResult>;
  onApproveIngest: (proposal: ProposedIngest) => Promise<string>;
  isLast: boolean;
}) {
  return (
    <div className="space-y-3">
      {/* user bubble */}
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-br-md bg-emerald-600 px-4 py-2 text-sm text-white">
          {turn.question}
        </div>
      </div>

      {/* assistant bubble */}
      <div className="flex items-start gap-3">
        <Spark small />
        <div className="min-w-0 flex-1">
          {turn.state === 'thinking' && (
            <div className="space-y-2">
              {turn.liveSteps && turn.liveSteps.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  {turn.liveSteps.map((s, i) => (
                    <span
                      key={`${s.summary}-${i}`}
                      className={`rounded-full border px-2 py-0.5 text-[11px] ${
                        s.ok
                          ? 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300'
                          : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300'
                      }`}
                    >
                      {s.summary}
                    </span>
                  ))}
                </div>
              )}
              <Thinking label={turn.statusLabel} />
            </div>
          )}
          {turn.state === 'error' && (
            <div className="rounded-2xl rounded-tl-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
              {turn.error}
            </div>
          )}
          {turn.state === 'done' && turn.preview && turn.previewFile && (
            <PreviewView
              preview={turn.preview}
              file={turn.previewFile}
              onIngest={onIngest}
              onRepreview={onRepreview}
              onConfirmProductLine={onConfirmProductLine}
            />
          )}
          {turn.state === 'done' && turn.answer && !turn.preview && (
            <div className="space-y-3">
              <AnswerView
                answer={turn.answer}
                onAsk={onAsk}
                onSave={onSave}
                onWatch={onWatch}
                onInvestigate={onInvestigate}
                showFollowUps={isLast && !turn.answer.proposedIngest}
              />
              {turn.answer.proposedIngest && (
                <IngestProposalView
                  proposal={turn.answer.proposedIngest}
                  onApprove={onApproveIngest}
                  onConfirmProductLine={onConfirmProductLine}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** The assistant's proposed ingest of an attached statement — the user approves
 *  (or answers the product-line question first). Nothing runs until Approve. */
function IngestProposalView({
  proposal,
  onApprove,
  onConfirmProductLine,
}: {
  proposal: ProposedIngest;
  onApprove: (proposal: ProposedIngest) => Promise<string>;
  onConfirmProductLine: (
    carrierId: string,
    choice: ProductLineChoice,
  ) => Promise<ProductLineConfirmResult>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plDone, setPlDone] = useState<ProductLineChoice | null>(null);
  const [plBusy, setPlBusy] = useState(false);
  const blocked = proposal.blockedByProductLine && !plDone;
  const periodLabel = `${MONTH_LABELS[proposal.periodMonth - 1]} ${proposal.periodYear}`;

  return (
    <div className="space-y-2 rounded-2xl border border-emerald-200 bg-emerald-50/50 px-4 py-3 text-sm dark:border-emerald-900 dark:bg-emerald-950/20">
      <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300">
        {proposal.replace ? 'Replace' : 'Ingest'} {proposal.fileName} as{' '}
        <b>{proposal.carrierName}</b> — {periodLabel}
        {proposal.periodLoaded ? ' (already loaded — this replaces it)' : ''}
      </p>
      {blocked && proposal.productLineQuestion && (
        <div className="space-y-1.5">
          <p className="text-xs text-amber-800 dark:text-amber-300">
            {proposal.productLineQuestion.prompt}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {proposal.productLineQuestion.options.map((o) => (
              <button
                key={o.value}
                type="button"
                disabled={plBusy}
                title={o.hint}
                onClick={async () => {
                  setPlBusy(true);
                  setError(null);
                  try {
                    await onConfirmProductLine(proposal.carrierId, o.value);
                    setPlDone(o.value);
                  } catch (e) {
                    setError(e instanceof Error ? e.message : 'Could not save the answer.');
                  } finally {
                    setPlBusy(false);
                  }
                }}
                className={`rounded-full border px-2.5 py-1 text-xs transition disabled:opacity-50 ${
                  o.value === proposal.productLineQuestion?.suggested
                    ? 'border-emerald-400 bg-white font-medium text-emerald-700 hover:bg-emerald-50 dark:border-emerald-600 dark:bg-neutral-900 dark:text-emerald-400'
                    : 'border-neutral-200 bg-white text-neutral-600 hover:border-emerald-300 hover:text-emerald-700 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {plDone && (
        <p className="text-xs text-emerald-700 dark:text-emerald-400">
          Got it — commission owed will be computed accordingly.
        </p>
      )}
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          disabled={busy || blocked}
          title={blocked ? 'Answer the product-line question first' : undefined}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await onApprove(proposal);
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Ingest failed.');
              setBusy(false);
            }
          }}
          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
        >
          {busy
            ? 'Queuing…'
            : `Approve — ${proposal.replace ? 'replace' : 'ingest'} ${periodLabel}`}
        </button>
        <span className="text-xs text-neutral-400">Nothing happens until you approve.</span>
      </div>
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  );
}

/** A statement dropped into the chat: what it is, how it fits, and one-click ingest. */
function PreviewView({
  preview,
  file,
  onIngest,
  onRepreview,
  onConfirmProductLine,
}: {
  preview: StatementPreview;
  file: File;
  onIngest: (file: File, carrierId: string, period: string) => Promise<string>;
  onRepreview: (file: File, carrierId: string) => Promise<void>;
  onConfirmProductLine: (
    carrierId: string,
    choice: ProductLineChoice,
  ) => Promise<ProductLineConfirmResult>;
}) {
  const { carriers } = useCarriers();
  const [carrierId, setCarrierId] = useState(preview.carrier?.id ?? '');
  const [period, setPeriod] = useState('');
  const [busy, setBusy] = useState(false);
  const [repreviewing, setRepreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Preflight product-line answer state (reset when the carrier changes).
  const [plAnswer, setPlAnswer] = useState<ProductLineConfirmResult | null>(null);
  const [plBusy, setPlBusy] = useState(false);
  const [plError, setPlError] = useState<string | null>(null);

  const plQuestion = preview.preflight?.questions.find((q) => q.id === 'product_line');
  const scheduleNotice = preview.preflight?.questions.find((q) => q.id === 'rate_schedule');
  // A blocking question gates ingest until answered — guardrails on the way in.
  const ingestBlocked = !!plQuestion?.blocking && !plAnswer;

  const switchCarrier = async (id: string) => {
    setCarrierId(id);
    setPlAnswer(null);
    setPlError(null);
    if (!id) return;
    setRepreviewing(true);
    setError(null);
    try {
      await onRepreview(file, id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not re-check against that carrier.');
    } finally {
      setRepreviewing(false);
    }
  };

  const answerProductLine = async (choice: ProductLineChoice) => {
    if (!carrierId || plBusy) return;
    setPlBusy(true);
    setPlError(null);
    try {
      setPlAnswer(await onConfirmProductLine(carrierId, choice));
    } catch (e) {
      setPlError(e instanceof Error ? e.message : 'Could not save the answer.');
    } finally {
      setPlBusy(false);
    }
  };

  const plLabel = (choice: ProductLineChoice) =>
    plQuestion?.options?.find((o) => o.value === choice)?.label ?? choice.replace(/_/g, ' ');

  const shapeText: Record<StatementPreview['shape'], string> = {
    matches_active: 'The layout matches the mapping on file — this will ingest cleanly.',
    different_layout:
      'The layout does NOT match the mapping on file. On ingest, a known mapping for this layout will be used or a new one will be built — nothing gets mis-mapped.',
    no_mapping: 'No mapping exists yet for this carrier — one will be built on first ingest.',
    unknown_carrier:
      'I couldn’t match this file to a carrier — pick one below, or set the carrier up first.',
  };

  const money = (v: number) =>
    `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-3 rounded-2xl rounded-tl-md bg-white px-4 py-3 text-sm leading-relaxed text-neutral-800 shadow-sm dark:bg-neutral-900 dark:text-neutral-100">
      <p>
        <b>{preview.fileName}</b> — {preview.sampleRows.toLocaleString()} data row
        {preview.sampleRows === 1 ? '' : 's'} ({preview.fileType.toUpperCase()}).{' '}
        {preview.carrier ? (
          <>
            Looks like <b>{preview.carrier.name}</b>
            {preview.detection.matchKind === 'signature'
              ? ' (known statement shape)'
              : preview.detection.matchKind === 'scored'
                ? ' (matched by content)'
                : ''}
            .
          </>
        ) : (
          'I couldn’t confidently match a carrier.'
        )}{' '}
        {shapeText[preview.shape]}
      </p>
      {preview.dryRun && (
        <p className="text-xs text-neutral-500">
          Mapping check: {preview.dryRun.mappedCount.toLocaleString()} of{' '}
          {preview.dryRun.rowCount.toLocaleString()} sample rows map cleanly, carrying{' '}
          {money(preview.dryRun.mappedTotal)} in commission.
        </p>
      )}
      {preview.statedTotal &&
        (preview.statedTotal.matchesMapped ? (
          <p className="text-xs text-emerald-700 dark:text-emerald-400">
            ✓ Matches the statement’s own printed total ({money(preview.statedTotal.value)}, “
            {preview.statedTotal.label}”).
          </p>
        ) : (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            ⚠ The statement prints {money(preview.statedTotal.value)} (“
            {preview.statedTotal.label}”) but the mapping carries{' '}
            {money(Math.abs(preview.statedTotal.variance))}{' '}
            {preview.statedTotal.variance < 0 ? 'less' : 'more'} — ingest will flag this variance.
          </p>
        ))}
      {preview.loadedPeriods.length > 0 && (
        <p className="text-xs text-neutral-500">
          Already loaded for this carrier: {preview.loadedPeriods.slice(-8).join(', ')}
          {preview.loadedPeriods.length > 8 ? '…' : ''} — uploading an existing month replaces it.
        </p>
      )}

      {/* Preflight: the product-line question, asked BEFORE ingest instead of guessing. */}
      {plQuestion && !plAnswer && (
        <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2.5 dark:border-amber-900 dark:bg-amber-950/30">
          <p className="text-xs text-amber-800 dark:text-amber-300">{plQuestion.prompt}</p>
          <div className="flex flex-wrap gap-1.5">
            {plQuestion.options?.map((o) => (
              <button
                key={o.value}
                type="button"
                disabled={plBusy || repreviewing}
                title={o.hint}
                onClick={() => answerProductLine(o.value)}
                className={`rounded-full border px-2.5 py-1 text-xs transition disabled:opacity-50 ${
                  o.value === plQuestion.suggested
                    ? 'border-emerald-400 bg-white font-medium text-emerald-700 hover:bg-emerald-50 dark:border-emerald-600 dark:bg-neutral-900 dark:text-emerald-400'
                    : 'border-neutral-200 bg-white text-neutral-600 hover:border-emerald-300 hover:text-emerald-700 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300'
                }`}
              >
                {o.label}
                {o.value === plQuestion.suggested ? ' ✓' : ''}
              </button>
            ))}
          </div>
          {plError && <p className="text-xs text-rose-600">{plError}</p>}
        </div>
      )}
      {plAnswer && (
        <p className="text-xs text-emerald-700 dark:text-emerald-400">
          Got it — {plLabel(plAnswer.choice)} it is.{' '}
          {plAnswer.rateSchedule.cmsFallback
            ? 'Owed will use the bundled CMS maximum rates (flagged as estimates) until you add your contracted rates.'
            : !plAnswer.rateSchedule.hasActive && plAnswer.productLine !== 'major_medical'
              ? 'Heads up: commission owed needs a rate schedule for this product line before it can compute.'
              : 'Commission owed will be computed accordingly.'}
        </p>
      )}
      {scheduleNotice && !plAnswer && (
        <p className="text-xs text-neutral-500">{scheduleNotice.prompt}</p>
      )}

      <div className="flex flex-wrap items-end gap-2 border-t border-neutral-100 pt-3 dark:border-neutral-800">
        <label className="text-xs text-neutral-500">
          Carrier
          <select
            value={carrierId}
            onChange={(e) => switchCarrier(e.target.value)}
            className="mt-0.5 block rounded-lg border border-neutral-200 bg-white px-2 py-1.5 text-sm dark:border-neutral-700 dark:bg-neutral-950"
          >
            <option value="">Pick a carrier…</option>
            {carriers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-neutral-500">
          Statement month
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="mt-0.5 block rounded-lg border border-neutral-200 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-950"
          />
        </label>
        <button
          type="button"
          disabled={busy || repreviewing || ingestBlocked || !carrierId || !period}
          title={ingestBlocked ? 'Answer the product-line question above first' : undefined}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await onIngest(file, carrierId, period);
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Upload failed.');
            } finally {
              setBusy(false);
            }
          }}
          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
        >
          {busy ? 'Uploading…' : repreviewing ? 'Re-checking…' : 'Ingest this statement'}
        </button>
      </div>
      {ingestBlocked && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          One quick answer needed above — the product line decides how commission owed is computed,
          so I don’t want to guess on your first ingest for this carrier.
        </p>
      )}
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  );
}

function Thinking({ label }: { label?: string }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-2xl rounded-tl-md bg-white px-4 py-3 shadow-sm dark:bg-neutral-900">
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2 w-2 animate-bounce rounded-full bg-emerald-400 [animation-delay:-0.3s]" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-emerald-400 [animation-delay:-0.15s]" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-emerald-400" />
      </span>
      {label && <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>}
    </div>
  );
}

/** The brand mark, reused as the assistant avatar / hero glyph. */
export function Spark({ large, small }: { large?: boolean; small?: boolean }) {
  const size = large ? 48 : small ? 28 : 36;
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-sm"
      style={{ width: size, height: size }}
    >
      <svg
        width={size * 0.58}
        height={size * 0.58}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path d="M12 2l2.2 6.3L20 10.5l-5.8 2.2L12 19l-2.2-6.3L4 10.5l5.8-2.2L12 2z" fill="white" />
      </svg>
    </span>
  );
}
